import * as React from 'react';
import { connect } from 'react-redux';
import { Route } from 'react-router';
import { HashRouter } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ServerService } from '../../../ts/services/ServerService';
import { UrlTelemetryService } from '../../../ts/services/UrlTelemetryService';
import { NavLocationSelectors } from '../../selectors/NavigationStateSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { ServerSpaceRouterComponent } from '../ServerSpaces/ServerSpaceRouter';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteSpaceRouterComponent } from './SiteSpaceRouter';

function getAllPaths(urls: SpacesSiteUrls) {
  const sitePaths = SiteSpaceRouterComponent.getWatchedBasePaths(urls);
  const allPaths = sitePaths.concat(ServerSpaceRouterComponent.getWatchedBasePaths());
  return allPaths;
}
const memoizedGetAllPaths = defaultMemoize(getAllPaths);

function isReactRouterPath(path: string, urls: SpacesSiteUrls): boolean {
  return _.some(memoizedGetAllPaths(urls), (baseUrl) => {

    // TFSID:955336 - should be removed when the viz page uses react router (vizRR)
    // /views (Site space) is a react-routerified page /views/someWB/someViewName (viz page) is _not_
    // so we only want to match and check our react router switch for /views and not for the viz page
    // we need to check that urls exist because they are undefined when just starting the app
    if (!FeatureFlags.vizRR && urls && baseUrl === urls.viewsPath()) {
      return path === baseUrl;
    }

    return path.startsWith(baseUrl);
  });
}

function stateNameForRR(path: string, spaceInfo: SpaceInfo, inServerArea: boolean) {
  return (inServerArea && path.startsWith('/server/') ? 'server.' : '') + spaceInfo.spaceType + (_.isUndefined(spaceInfo.subspaceName) ? '' : '.' + spaceInfo.subspaceName);
}

export interface TelemetryRouterOwnProps {
  readonly locationPath: string;
  readonly telemetryService: UrlTelemetryService;
  readonly currentAngularStateName: string;
}

export interface TelemetryRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly spaceInfo: SpaceInfo;
  readonly inServerArea: boolean;
}

export type TelemetryRouterProps = TelemetryRouterOwnProps & TelemetryRouterStateProps;

interface TelemetryRouterState {
  readonly previousStateName: string;
}

export class TelemetryRouter extends React.PureComponent<TelemetryRouterProps, TelemetryRouterState> {
  public render(): JSX.Element {
    return null;
  }

  public constructor(props: TelemetryRouterProps) {
    super(props);
    this.state = { previousStateName: undefined };
  }

  public componentDidUpdate(prevProps: TelemetryRouterProps) {
    // In a react router world we can remove all the angular stuff from this file,
    // along with the router stuff, all we will need is the space info changes from app store
    if (prevProps.spaceInfo !== this.props.spaceInfo || this.props.locationPath !== prevProps.locationPath
      || this.props.currentAngularStateName !== prevProps.currentAngularStateName) {

        const currentReactRouter = this.props.urls && isReactRouterPath(this.props.locationPath, this.props.urls);
        if (currentReactRouter && _.isUndefined(this.props.spaceInfo.spaceType)) {
          // special case if we're on react router page but invalid spaceinfo
          // means space info hasn't been set yet. Just return and wait.
          return;
        }

        const currentStateName = currentReactRouter
          ? stateNameForRR(this.props.locationPath, this.props.spaceInfo, this.props.inServerArea)
          : this.props.currentAngularStateName;

        // dont send if prev/current angular state name have reactrouter but are not equal
        // from server react router angular state to site react router angular state
        const reactToDifferentReact = this.props.currentAngularStateName.includes('reactrouter')
          && prevProps.currentAngularStateName.includes('reactrouter')
          && this.props.currentAngularStateName !== prevProps.currentAngularStateName;

        if (this.state.previousStateName !== currentStateName && currentStateName
          && !currentStateName.includes('reactrouter') && !reactToDifferentReact) {
            this.props.telemetryService.sendUrlEventsOnStateChange({
              newStateName: currentStateName,
              oldStateName: this.state.previousStateName
            });
            this.setState({previousStateName: currentStateName});
        }
    }
  }
}

const mapStateToProps: MapStateToProps<TelemetryRouterStateProps> = state => ({
  spaceInfo: selectSpaceInfo(state),
  urls: siteUrlsSelector(state),
  inServerArea: NavLocationSelectors.inServerArea(state),
});

export const ConnectedTelemetryRouter
  = connect(mapStateToProps)(TelemetryRouter);

export const NavigationTelemetry = (props: TelemetryRouterOwnProps) => {
  const telemetryService = ServiceLocator.get<UrlTelemetryService>('UrlTelemetryService');

  const [currentAngularStateName, setCurrentAngularStateName] = React.useState('');
  React.useEffect(() => {
    ServiceLocator.get<ng.IRootScopeService>('$rootScope').$on('$stateChangeSuccess', (
      _event: ng.IAngularEvent,
      toState: ng.ui.IState,
    ) => {
      setCurrentAngularStateName(toState ? toState.name : '');
    });
  }, []);

  // TFSID:997680. Don't render the HashRouter until we've signed in.
  const server = ServiceLocator.get<ServerService>('ServerService');
  if (!server.inSession()) {
    return null;
  }

  return (
    <HashRouter>
      <Route render={(routeProps) =>

        <ConnectedTelemetryRouter
          locationPath={routeProps.location.pathname}
          telemetryService={telemetryService}
          currentAngularStateName={currentAngularStateName}
        />
      }/>
    </HashRouter>
  );
};
